/**
 * Customer signal bus. The customer's product code can push typed events to
 * the agent via `window.IraaDemo.signal({event, payload})` — the agent then
 * sees those events in its prompt the next turn. Closes the gap where the
 * DOM is a lossy projection of app state (Stripe webhook returned, license
 * issued, "saved" toast that lives 200ms — too short for the verifier to
 * catch).
 *
 * Lives on the parent page next to the widget Web Component. The iframe-side
 * has its own observation pipeline (PageSnapshot / ScreenObservation) — we
 * don't merge them here; both are surfaced separately to the prompt.
 *
 * Threat model: the customer's site is trusted (they installed the widget).
 * We don't sign these events end-to-end yet. Validation here is shape-only:
 * non-empty event name, small payload, capped count. A rogue extension on
 * the customer's page could spam events; the cap + whitelist limit blast
 * radius to "the agent's prompt has noisy CUSTOMER SIGNALS for one session".
 */

/** Single event ringbuffer entry. Wire-shape; do not change without server. */
export interface CustomerSignal {
  /** Wall-clock ms when the event was pushed. */
  ts: number;
  /**
   * Event name. Customer-defined. Suggested namespacing: `domain.action`
   * (e.g. `auth.login_succeeded`, `cart.item_added`, `checkout.complete`).
   * Capped at 80 chars.
   */
  event: string;
  /**
   * Optional structured payload. JSON-serializable. Capped at ~1KB
   * stringified to keep prompt footprint bounded — larger payloads are
   * rejected at push time.
   */
  payload?: Record<string, unknown>;
}

const RING_CAP = 32;
const MAX_EVENT_NAME_LEN = 80;
const MAX_PAYLOAD_BYTES = 1024;
const EVENT_NAME_RE = /^[a-zA-Z][a-zA-Z0-9._:-]{0,79}$/;

let ring: CustomerSignal[] = [];

/**
 * Push an event into the ring buffer. Validates shape; silently drops bad
 * events (we don't surface errors to customer code — broken instrumentation
 * shouldn't break the demo).
 */
export function pushSignal(input: { event: unknown; payload?: unknown }): boolean {
  if (typeof input?.event !== 'string') return false;
  const name = input.event.slice(0, MAX_EVENT_NAME_LEN);
  if (!EVENT_NAME_RE.test(name)) return false;

  let payload: Record<string, unknown> | undefined;
  if (input.payload !== undefined) {
    if (input.payload === null || typeof input.payload !== 'object') return false;
    let serialized: string;
    try {
      serialized = JSON.stringify(input.payload);
    } catch {
      return false; // circular refs, BigInts, etc.
    }
    if (serialized.length > MAX_PAYLOAD_BYTES) return false;
    payload = input.payload as Record<string, unknown>;
  }

  ring.push({ ts: Date.now(), event: name, payload });
  if (ring.length > RING_CAP) ring.splice(0, ring.length - RING_CAP);
  return true;
}

/**
 * Drain pending signals on each chat-turn. Call from the live-demo
 * orchestrator before posting the visitor message so the server sees the
 * events that occurred since the last turn.
 *
 * Returns up to the most-recent N events (caller decides; default 8 keeps
 * prompt footprint tight). Drained events are removed from the buffer.
 */
export function drainSignals(limit = 8): CustomerSignal[] {
  if (ring.length === 0) return [];
  const out = ring.slice(-limit);
  ring = [];
  return out;
}

/** Test-only / mode-switch hook to clear the ring without reading. */
export function resetSignalBus(): void {
  ring = [];
}

/**
 * Install `window.IraaDemo.signal({event, payload})` on the parent page.
 * Idempotent — safe to call multiple times. Returns the IraaDemo namespace
 * for callers that want a typed handle.
 *
 * We also install a `version` and a flag so customer code can feature-detect
 * (`if (window.IraaDemo?.signal) ...`) without crashing on older builds.
 */
export interface IraaDemoNamespace {
  version: string;
  signal: (input: { event: string; payload?: Record<string, unknown> }) => boolean;
}

export function installSignalBus(version = '1'): IraaDemoNamespace {
  const ns: IraaDemoNamespace = {
    version,
    signal: (input) => pushSignal(input as { event: unknown; payload?: unknown }),
  };
  try {
    const existing = (window as unknown as { IraaDemo?: IraaDemoNamespace }).IraaDemo;
    // Don't clobber a future, richer namespace from another widget version
    // (rare but possible during cdn rollouts). Merge `signal` onto whatever's there.
    if (existing && typeof existing === 'object') {
      Object.assign(existing, { signal: ns.signal });
      // Preserve whichever version was set first (older builds win — they're
      // less likely to remove APIs the customer code is calling).
      if (!existing.version) existing.version = version;
      return existing;
    }
    (window as unknown as { IraaDemo: IraaDemoNamespace }).IraaDemo = ns;
  } catch {
    // SSR / no window — caller is in a non-browser context, skip silently.
  }
  return ns;
}
